import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import ExamProcessDialog from "./ExamProcessDialog";
import ConfigData from "../config/config.json";

const webserver = ConfigData.WEBSERVER;

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "ID",
  },
  {
    id: "startingDate",
    numeric: false,
    disablePadding: false,
    label: "Start Datum",
  },
  {
    id: "closingDate",
    numeric: false,
    disablePadding: false,
    label: "End Datum",
  },
  {
    id: "amountOfPharms",
    numeric: false,
    disablePadding: false,
    label: "Fortschritt",
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function ExamProcessTable() {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const [rowss, setRowss] = useState([]);

  //
  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/examProcess/getAllArchived",
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].deleteOpen = false;
        }

        data.forEach((examProcess, index) => {
          let temp = [];

          examProcess.examiners.forEach((examiner, index) => {
            temp[index] = examiner.userName;
          });
          examProcess.examiners = temp;
        });
        setRowss(data);
      })
      .catch((error) => {});
  }, []);

  const checkExaminers = (examiners) => {
    let includes = false;

    examiners.forEach((examiner) => {
      if (examiner.toLowerCase().includes(searchQuery.toLowerCase())) {
        includes = true;
      }
    });
    return includes;
  };

  const filteredRows = rowss.filter((row) => {
    return (
      row.startingDate
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      row.closingDate
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      checkExaminers(row.examiners)
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowss.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRowss={setRowss}
              rowss={rowss}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rowss.length; found++) {
                    if (rowss[found].id === row.id) {
                      index = found;
                      break;
                    }
                  }

                  let examinedNumber = 0;
                  for (
                    let resultIndex = 0;
                    resultIndex < row.results.length;
                    resultIndex++
                  ) {
                    if (
                      row.results[resultIndex].result === "PASSED" ||
                      row.results[resultIndex].result ===
                        "PASSED_AFTER_FAILURE" ||
                      row.results[resultIndex].result === "FAILED" ||
                      row.results[resultIndex].result === "MULTIPLE_FAILURES"
                    ) {
                      examinedNumber++;
                    }
                  }

                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <>
                      <ExamProcessDialog
                        archive={true}
                        function={() => {}}
                        open={row.listOpen}
                        setOpen={() =>
                          setRowss((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].listOpen = false;
                            return temp;
                          })
                        }
                        id={row.id}
                      />
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          setRowss((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].listOpen = true;
                            return temp;
                          })
                        }
                      >
                        <TableCell id={labelId} scope="row">
                          {row.id}
                        </TableCell>
                        <TableCell align="left">
                          {row.startingDateString}
                        </TableCell>
                        <TableCell align="left">
                          {row.closingDateString}
                        </TableCell>
                        <TableCell align="left">
                          {examinedNumber} / {row.pharmacies.length}
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowss.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
